import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiRequest } from '../../services/api';
import './SubscriptionPage.css';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import SubscriptionSuccessPopup from './SubscriptionSuccessPopup';
import SubscriptionWarningPopup from './SubscriptionWarningPopup';
import SubscriptionContractModal from './SubscriptionContractModal';

const SubscriptionPage = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
    const [pendingModalPlan, setPendingModalPlan] = useState<{ id: string; name: string; price: string; duration: string } | null>(null);
    const [showSuccessPopup, setShowSuccessPopup] = useState(false);
    const [showWarningPopup, setShowWarningPopup] = useState(false);
    const [showContractModal, setShowContractModal] = useState(false);
    const [warningTargetPlan, setWarningTargetPlan] = useState('');
    const [warningType, setWarningType] = useState<'downgrade' | 'same_plan'>('downgrade');
    const [activationCode, setActivationCode] = useState('');
    const [timeLeft, setTimeLeft] = useState<number | null>(null);
    const [timerActive, setTimerActive] = useState(false);

    const plans = [
        {
            id: 'monthly',
            name: 'Aylık',
            price: '₺150',
            duration: '1 Ay',
            features: ['Tüm Özellikler', 'Sınırsız İşyeri', '7/24 Destek', 'Aylık Ödeme'],
            color: '#3498db'
        },
        {
            id: 'semiyearly',
            name: '6 Aylık',
            price: '₺750',
            duration: '6 Ay',
            features: ['1 Ay Ücretsiz', 'Tüm Özellikler', 'Sınırsız İşyeri', 'Öncelikli Destek'],
            color: '#9b59b6',
            popular: true
        },
        {
            id: 'yearly',
            name: 'Yıllık',
            price: '₺1.500',
            duration: '1 Yıl',
            features: ['2 Ay Ücretsiz', 'Tüm Özellikler', 'Sınırsız İşyeri', 'VIP Destek'],
            color: '#2ecc71'
        }
    ];

    const { 
        checkSubscription, subscriptionPlan, isActive, daysRemaining, 
        pendingPlan, pendingPlanExpiresAt, loading: subLoading 
    } = useSubscription();
 
    useEffect(() => {
        checkSubscription();
    }, []);

    // RESTORE TIMER FROM BACKEND DATA
    useEffect(() => {
        if (pendingPlan && pendingPlanExpiresAt) {
            setSelectedPlan(pendingPlan);
            
            const expiresAt = new Date(pendingPlanExpiresAt).getTime();
            const now = new Date().getTime();
            const remaining = Math.max(0, Math.floor((expiresAt - now) / 1000));
            
            if (remaining > 0) {
                setTimeLeft(remaining);
                setTimerActive(true);
            } else {
                setTimeLeft(0);
                setTimerActive(false);
            }
        } else {
            // STOP TIMER IF NO PENDING PLAN (e.g. admin rejected)
            setTimerActive(false);
            setTimeLeft(null);
            setSelectedPlan(null);
        }
    }, [pendingPlan, pendingPlanExpiresAt]);

    useEffect(() => {
        let interval: NodeJS.Timeout;
        if (timerActive && timeLeft !== null && timeLeft > 0) {
            interval = setInterval(() => {
                setTimeLeft(prev => (prev !== null ? prev - 1 : null));
            }, 1000);
        } else if (timeLeft === 0 && timerActive) {
            setTimerActive(false);
            // Automatic re-request when timer expires
            if (selectedPlan) {
                executeSubscribeRequest(selectedPlan);
                alert('Süre dolduğu için aktivasyon talebiniz güncellendi. Lütfen bekleyiniz.');
            }
        }
        return () => clearInterval(interval);
    }, [timerActive, timeLeft]);

    // SYNC STATUS FROM BACKEND (In case admin rejects/activates)
    useEffect(() => {
        let syncInterval: NodeJS.Timeout;
        if (timerActive) {
            syncInterval = setInterval(() => {
                checkSubscription();
            }, 10000); // Check every 10 seconds
        }
        return () => clearInterval(syncInterval);
    }, [timerActive]);

    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
    };

    const executeSubscribeRequest = async (planId: string) => {
        setSelectedPlan(planId);
        try {
            await apiRequest('/subscription/request-activation', 'POST', { planId });
            await checkSubscription();
        } catch (error: any) {
            const errorMsg = error.response?.data?.message || 'Aktivasyon talebi iletilemedi.';
            alert(errorMsg);
            await checkSubscription();
        }
    };

    const handlePlanSelectClick = (planId: string) => {
        // Zaten aktif bir geri sayım veya bekleyen talep varsa engelle ve uyar
        if (timerActive && selectedPlan) {
            const planObj = plans.find(p => p.id === selectedPlan);
            alert(`Şu anda zaten "${planObj ? planObj.name : selectedPlan}" için aktif bir talebiniz bulunmaktadır. Lütfen sayacın bitmesini bekleyiniz veya yöneticinizden aktivasyon kodunu talep ediniz.`);
            return;
        }

        const planObj = plans.find(p => p.id === planId);
        if (planObj) {
            setPendingModalPlan({
                id: planObj.id,
                name: planObj.name,
                price: planObj.price,
                duration: planObj.duration
            });
            setShowContractModal(true);
        }
    };

    const handleConfirmContractAndSubscribe = async () => {
        if (!pendingModalPlan) return;
        const planId = pendingModalPlan.id;
        setShowContractModal(false);
        setPendingModalPlan(null);
        await executeSubscribeRequest(planId);
    };

    const handleActivateCode = async () => {
        if (!activationCode) return;
        
        try {
            setLoading(true);
            
            // expectedPlan is the plan user currently has selected on the UI
            const response = await apiRequest('/subscription/activate', 'POST', { 
                code: activationCode,
                expectedPlan: selectedPlan
            });
            
            if (response.success) {
                setActivationCode('');
                setShowSuccessPopup(true);
            } else {
                alert(response.message || 'Geçersiz aktivasyon kodu');
                setActivationCode(''); // Clear on failure too
                setLoading(false);
            }
        } catch (error: any) {
            alert('Aktivasyon hatası: ' + (error.message || 'Kod geçersiz veya kullanılmış'));
            setActivationCode(''); // Clear on error too
            setLoading(false);
        }
    };

    const getPlanName = (planId: string | null) => {
        const found = plans.find(p => p.id === planId);
        return found ? found.name : (planId === 'trial' ? 'Deneme Sürümü' : 'Bilinmeyen Paket');
    };

    const handlePopupClose = () => {
        setShowSuccessPopup(false);
        checkSubscription();
        navigate('/dashboard');
    };

    return (
        <div className="subscription-page">

            <div className={`status-card ${isActive ? 'active' : 'expired'}`}>
                {subLoading ? (
                    <div className="status-loading">
                        <i className="fas fa-spinner fa-spin"></i> Abonelik bilgileri yükleniyor...
                    </div>
                ) : (
                    <>
                        <div className="status-header">
                            <i className={`fas ${isActive ? 'fa-check-circle' : 'fa-exclamation-circle'}`}></i>
                            <span>Mevcut Durum: {isActive ? 'Aktif' : 'Pasif'}</span>
                            <button
                                onClick={() => checkSubscription()}
                                style={{
                                    marginLeft: 'auto',
                                    padding: '4px 10px',
                                    backgroundColor: '#3498db',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '5px',
                                    cursor: 'pointer',
                                    fontSize: '14px'
                                }}
                            >
                                <i className="fas fa-sync-alt"></i> Yenile
                            </button>
                        </div>
                        <div className="status-body">
                            <div className="status-item">
                                <span className="label">Paket:</span>
                                <span className="value plan-blink" style={{ 
                                    color: plans.find(p => p.id === subscriptionPlan)?.color || '#34495e',
                                    fontWeight: 'bold'
                                }}>
                                    {getPlanName(subscriptionPlan)}
                                </span>
                            </div>
                            <div className="status-item">
                                <span className="label">Kalan Süre:</span>
                                {(() => {
                                    const days = daysRemaining !== null ? daysRemaining : 0;
                                    let daysClass = 'days-green';
                                    if (days <= 14) {
                                        daysClass = 'days-red-blink';
                                    } else if (days <= 30) {
                                        daysClass = 'days-yellow-blink';
                                    }
                                    return (
                                        <span className={`value ${daysClass}`}>
                                            {`${days} Gün`}
                                        </span>
                                    );
                                })()}
                            </div>
                        </div>
                    </>
                )}
            </div>

            <div className="plans-container">
                {plans.map((plan) => (
                    <div 
                        key={plan.id} 
                        className={`plan-card ${plan.popular ? 'popular' : ''}`}
                        style={{ borderColor: plan.color }}
                    >
                        {plan.popular && <div className="popular-badge">En Popüler</div>}
                        <div className="plan-header" style={{ borderBottomColor: plan.color }}>
                            <h2 style={{ color: plan.color }}>{plan.name}</h2>
                            <div className="plan-price">{plan.price}<span>/{plan.duration}</span></div>
                        </div>
                        <ul className="plan-features">
                            {plan.features.map((feature, index) => (
                                <li key={index}><i className="fas fa-check"></i> {feature}</li>
                            ))}
                        </ul>
                        <button
                            className={`plan-button ${selectedPlan === plan.id ? 'selected' : ''}`}
                            style={{ backgroundColor: plan.color }}
                            onClick={() => handlePlanSelectClick(plan.id)}
                            disabled={loading}
                        >
                            {selectedPlan === plan.id ? 'Seçildi' : 'Planı Seç'}
                        </button>
                    </div>
                ))}
            </div>

            <div id="manual-payment-section" className="manual-payment-section">
                <div className="bank-details-card">
                    <h3><i className="fas fa-university"></i> Banka Havalesi / EFT</h3>
                    <p>Paketinizi havale/EFT ile uzatmak isterseniz aşağıdaki hesap numarasına ödeme yapıp dekontu bize iletebilirsiniz.</p>
                    <div className="bank-info-grid">
                        <div className="bank-item">
                            <span className="label">Banka:</span>
                            <span className="value">Ziraat Bankası</span>
                        </div>
                        <div className="bank-item">
                            <span className="label">Alıcı:</span>
                            <span className="value">AYHAN ASLAN</span>
                        </div>
                        <div className="bank-item">
                            <span className="label">IBAN:</span>
                            <span className="value iban">TR00 0000 0000 0000 0000 0000 00</span>
                        </div>
                    </div>
                    <p className="note"><strong>**Önemli:</strong> Açıklama kısmına firma adınızı yazmayı unutmayınız.</p>
                    
                    <div className="whatsapp-contact">
                        <p style={{ fontWeight: 'bold', color: '#000000' }}>Ödeme sonrası dekontu bu numaraya WhatsApp üzerinden iletiniz:</p>
                        <a 
                            href="https://wa.me/905072136474?text=Merhaba,%20abonelik%20dekontumu%20iletmek%20istiyorum." 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="whatsapp-button"
                        >
                            <i className="fab fa-whatsapp"></i>
                            0507 213 64 74
                        </a>
                    </div>
                </div>

                <div className={`activation-code-card ${(timerActive || (timeLeft === 0 && selectedPlan)) ? 'highlight-flash' : ''}`}>
                    <div className="card-header-row">
                        <h3><i className="fas fa-key"></i> Aktivasyon Kodu</h3>
                        {timeLeft !== null && (
                            <div className={`countdown-timer-mini ${timeLeft === 0 ? 'expired' : (timeLeft < 300 ? 'urgent' : '')}`}>
                                <i className="fas fa-clock"></i>
                                <span>{timeLeft === 0 ? 'Süre Doldu' : formatTime(timeLeft)}</span>
                            </div>
                        )}
                    </div>
                    <p>Ödemeniz onaylandığında size iletilecek olan kodu buraya girerek paketinizi anında aktif edebilirsiniz.</p>
                    <div className="code-input-group">
                        <input 
                            type="text" 
                            placeholder="Örn: UZMAN-ABCD-1234"
                            value={activationCode}
                            onChange={(e) => setActivationCode(e.target.value.toUpperCase())}
                            disabled={loading}
                        />
                        <button 
                            onClick={handleActivateCode}
                            disabled={loading || !activationCode}
                        >
                            {loading ? 'Aktifleştiriliyor...' : 'Paketi Aktif Et'}
                        </button>
                    </div>

                    <div style={{ marginTop: '14px', fontSize: '0.8rem', color: '#64748b', textAlign: 'center', lineHeight: '1.5' }}>
                        Paket aktivasyonunuz ile <button type="button" onClick={() => { setPendingModalPlan(plans[0]); setShowContractModal(true); }} style={{ background: 'none', border: 'none', color: '#2563eb', textDecoration: 'underline', cursor: 'pointer', padding: 0, font: 'inherit' }}>Ön Bilgilendirme ve Mesafeli Satış Sözleşmesi</button> şartlarını kabul etmiş sayılırsınız.
                    </div>
                </div>
            </div>

            <SubscriptionContractModal
                isOpen={showContractModal}
                onClose={() => setShowContractModal(false)}
                onConfirm={handleConfirmContractAndSubscribe}
                plan={pendingModalPlan}
                loading={loading}
            />

            <SubscriptionSuccessPopup isOpen={showSuccessPopup} onClose={handlePopupClose} />
            <SubscriptionWarningPopup
                isOpen={showWarningPopup}
                onClose={() => setShowWarningPopup(false)}
                currentPlanName={getPlanName(subscriptionPlan)}
                targetPlanName={warningTargetPlan}
                warningType={warningType}
            />
        </div>
    );
};

export default SubscriptionPage;
